/* eslint-disable max-len */
import { Col, Row, message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { type ConfiguratorModuleInDealerSpecsFragment } from '../../../../api';
import { type ApplicationMarketTypeFragmentFragment } from '../../../../api/fragments/ApplicationMarketTypeFragment';
import { type DealerWithPermissionsFragmentFragment } from '../../../../api/fragments/DealerWithPermissionsFragment';
import { useUpdateApplicationDealershipAssignmentsByDealerMutation } from '../../../../api/mutations/updateApplicationDealershipAssignmentsByDealer';
import {
    type UpdateConfiguratorModuleEmailContentsMutationVariables,
    useUpdateConfiguratorModuleEmailContentsMutation,
} from '../../../../api/mutations/updateConfiguratorModuleEmailContents';
import { useGetVariantsOfAllConfiguratorsQuery } from '../../../../api/queries/getVariantsOfAllConfigurators';
import { useListUsersQuery } from '../../../../api/queries/listUsers';
import {
    ContentRefinementSourceAction,
    ContentRefinementSourceKind,
    EmailContentUpdateType,
    type TranslatedStringInput,
    VehicleKind,
} from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import Form from '../../../../components/fields/Form';
import PriceDisclaimerField from '../../../../components/fields/PriceDisclaimerField';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import useHandleError from '../../../../utilities/useHandleError';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import useValidator from '../../../../utilities/useValidator';
import ConfiguratorModuleEmailForm from '../../ModuleDetailsPage/ConfiguratorModulePage/ConfiguratorModuleEmailForm';
import { type ConfiguratorEmailFormValues } from '../../ModuleDetailsPage/ConfiguratorModulePage/type';
import useHandleConfiguratorModuleAssets from '../../ModuleDetailsPage/ConfiguratorModulePage/useHandleConfiguratorModuleAssets';
import FinanceProductAssignmentSection from './FinanceProductAssignmentSection';
import InsuranceProductAssignmentSection from './InsuranceProductAssignmentSection';
import MarketTypeEditableField, {
    type DealerMarketType,
    dealerMarketTypeValidations,
    getInitDealerMarketType,
    pickDealerMarketType,
} from './MarketTypeEditableField';
import VehicleListSection from './VehicleListSection';

export type DealerAssignmentConfiguratorFormProps = {
    module: ConfiguratorModuleInDealerSpecsFragment;
    dealer: DealerWithPermissionsFragmentFragment;
};

type FormValues = {
    dealerFinanceProductSuiteIds: string[];
    dealerAssignee: string;
    dealerPriceDisclaimer: TranslatedStringInput[];
    dealerMarketType: DealerMarketType;
    emailContents: ConfiguratorEmailFormValues;
    dealerTermsTitle: TranslatedStringInput;
    dealerTermsText: TranslatedStringInput;
    dealerInsuranceProductSuiteIds: string[];
};

const createValidations = dealerMarketTypeValidations;

const DealerAssignmentConfiguratorForm = ({ module, dealer }: DealerAssignmentConfiguratorFormProps) => {
    const { t } = useTranslation(['dealershipManagement', 'configuratorModuleDetail', 'moduleDetails']);
    const [mutation] = useUpdateApplicationDealershipAssignmentsByDealerMutation();
    const [updateEmailContentMutation] = useUpdateConfiguratorModuleEmailContentsMutation();

    const handleConfiguratorModuleAssets = useHandleConfiguratorModuleAssets();

    const marketType = module.marketType as ApplicationMarketTypeFragmentFragment;

    // initialize finance products and dealers for the dealer
    const initialValues: FormValues = useMemo(
        () => ({
            dealerFinanceProductSuiteIds:
                module.dealerFinanceProducts.find(({ dealerId }) => dealerId === dealer.id)?.financeProductSuiteIds ||
                [],
            dealerAssignee: module.assignee.overrides.find(({ dealerId }) => dealerId === dealer.id)?.relatedId,
            dealerPriceDisclaimer:
                module.priceDisclaimer.overrides.find(({ dealerId }) => dealerId === dealer.id)?.value ?? [],
            dealerMarketType: getInitDealerMarketType(marketType, dealer.id),
            emailContents: module.emailContents,
            dealerTermsTitle: module.termsTitle.overrides.find(({ dealerId }) => dealerId === dealer.id)?.value ?? {
                defaultValue: '',
                overrides: [],
            },
            dealerTermsText: module.termsText.overrides.find(({ dealerId }) => dealerId === dealer.id)?.value ?? {
                defaultValue: '',
                overrides: [],
            },
            dealerInsuranceProductSuiteIds:
                module.dealerInsuranceProducts.find(({ dealerId }) => dealerId === dealer.id)
                    ?.insuranceProductSuiteIds || [],
        }),
        [dealer.id, module, marketType]
    );

    const { data, loading } = useListUsersQuery({
        fetchPolicy: 'cache-and-network',
    });

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(dealer.permissions, [permissionKind.updateDealer]),
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [dealer.permissions, module.id]
    );

    const salesPersonOptions = useMemo(
        () =>
            (data?.list?.items || []).map(user => ({
                label: user.displayName,
                value: user.id,
            })),
        [data]
    );

    const { data: vehiclesData } = useGetVariantsOfAllConfiguratorsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { moduleId: module.id },
    });

    const vehicleSuiteIds = useMemo<string[] | null>(() => {
        if (!vehiclesData?.vehicles) {
            return null;
        }

        return vehiclesData.vehicles
            .map(vehicle => {
                switch (vehicle.__typename) {
                    case VehicleKind.LocalVariant:
                        return vehicle.versioning.suiteId;

                    default:
                        return null;
                }
            })
            .filter(item => item !== null);
    }, [vehiclesData]);

    const validator = useMemo(() => createValidations(marketType), [marketType]);
    const validate = useValidator(validator);

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const {
                dealerFinanceProductSuiteIds,
                dealerAssignee,
                dealerPriceDisclaimer,
                dealerTermsTitle,
                dealerTermsText,
                dealerMarketType,
                emailContents,
                dealerInsuranceProductSuiteIds,
            } = values;

            const { submitOrder, saveOrder } = emailContents;
            const { introImage: saveOrderIntroImage, bannerImage, ...saveOrderSetting } = saveOrder;
            const { introImage: submitOrderIntroImage, ...submitOrderSetting } = submitOrder;

            // submitting message
            message.loading({
                content: t('dealershipManagement:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const dealerVehicles = { dealerId: dealer.id, vehicleSuiteIds };
            const dealerFinanceProducts = { dealerId: dealer.id, financeProductSuiteIds: dealerFinanceProductSuiteIds };
            const dealerInsuranceProducts = {
                dealerId: dealer.id,
                insuranceProductSuiteIds: dealerInsuranceProductSuiteIds,
            };

            const variables = {
                moduleId: module.id,
                dealerVehicles,
                dealerFinanceProducts,
                dealerInsuranceProducts,
                ...(dealerAssignee && {
                    dealerAssignee: {
                        dealerId: dealer.id,
                        relatedId: dealerAssignee,
                    },
                }),
                ...(dealerPriceDisclaimer && {
                    dealerPriceDisclaimer: {
                        dealerId: dealer.id,
                        value: dealerPriceDisclaimer,
                    },
                }),
                dealerTermsText: {
                    dealerId: dealer.id,
                    value: dealerTermsText,
                },
                dealerTermsTitle: {
                    dealerId: dealer.id,
                    value: dealerTermsTitle,
                },
                dealerMarketType: {
                    dealerId: dealer.id,
                    ...pickDealerMarketType(marketType, dealerMarketType),
                },
            };

            const updateEmailContentVariables: UpdateConfiguratorModuleEmailContentsMutationVariables = {
                moduleId: module.id,
                settings: {
                    emailContentUpdateType: EmailContentUpdateType.Dealer,
                    dealerId: dealer.id,
                    saveOrder: saveOrderSetting,
                    submitOrder: submitOrderSetting,
                },
            };

            await mutation({ variables })
                .then(() => updateEmailContentMutation({ variables: updateEmailContentVariables }))
                .finally(() => {
                    message.destroy('primary');
                });

            await handleConfiguratorModuleAssets(
                module.id,
                { submitOrder, saveOrder },
                EmailContentUpdateType.Dealer,
                module.emailContents,
                dealer.id
            );

            // show successful message
            message.success({
                content: t('dealershipManagement:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [
            t,
            dealer.id,
            vehicleSuiteIds,
            module.id,
            module.emailContents,
            marketType,
            mutation,
            handleConfiguratorModuleAssets,
            updateEmailContentMutation,
        ]
    );

    const formId = `dealerForm-${module.id}`;

    const markdownInfoTooltip = useMarkDownInfoTooltip(marketType.type);
    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <Formik<FormValues>
                initialValues={initialValues}
                onSubmit={onSubmit}
                validate={validate}
                enableReinitialize
            >
                {({ handleSubmit }) => (
                    <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                        <CollapsibleWrapper defaultActiveKey={['configuration']}>
                            <Panel key="configuration" header={t('configuratorModuleDetail:subTitles.configuration')}>
                                <Row gutter={10}>
                                    <Col md={8} xs={24}>
                                        <FormFields.SelectField
                                            {...t('configuratorModuleDetail:fields.assignee', { returnObjects: true })}
                                            disabled={!hasUpdatePermission}
                                            loading={loading}
                                            name="dealerAssignee"
                                            options={salesPersonOptions}
                                            showSearch
                                        />
                                    </Col>
                                    <Col md={8} xs={24}>
                                        <PriceDisclaimerField
                                            {...t('moduleDetails:fields.priceDisclaimerTitle', { returnObjects: true })}
                                            disabled={!hasUpdatePermission}
                                            name="dealerPriceDisclaimer"
                                            tooltip={markdownInfoTooltip}
                                        />
                                    </Col>
                                    <MarketTypeEditableField
                                        disabled={!hasUpdatePermission}
                                        marketType={module.marketType}
                                    />
                                    <Col md={8} xs={24}>
                                        <FormFields.TranslatedInputField
                                            {...t('moduleDetails:fields.termsTitle', { returnObjects: true })}
                                            disabled={!hasUpdatePermission}
                                            name="dealerTermsTitle"
                                        />
                                    </Col>
                                    <Col md={8} xs={24}>
                                        <TranslatedTextAreaField
                                            {...t('moduleDetails:fields.termsText', { returnObjects: true })}
                                            disabled={!hasUpdatePermission}
                                            name="dealerTermsText"
                                        />
                                    </Col>
                                </Row>
                            </Panel>
                        </CollapsibleWrapper>

                        <VehicleListSection
                            disabled={!hasUpdatePermission}
                            header={t('dealershipManagement:tabs.panel.vehicleAssignment')}
                            name="configuratorVehicleIds"
                            vehicles={vehiclesData?.vehicles || []}
                        />
                        <FinanceProductAssignmentSection
                            disabled={!hasUpdatePermission}
                            moduleId={module.bankModuleId}
                            variantSuiteIds={vehicleSuiteIds}
                        />
                        {module.insuranceModuleId && (
                            <InsuranceProductAssignmentSection
                                disabled={!hasUpdatePermission}
                                moduleId={module.insuranceModuleId}
                                variantSuiteIds={vehicleSuiteIds}
                            />
                        )}
                        <CollapsibleWrapper defaultActiveKey={['email']}>
                            <Panel key="email" header={t('configuratorModuleDetail:tabs.email')}>
                                <ConfiguratorModuleEmailForm
                                    disabled={!hasUpdatePermission}
                                    prefix="emailContents"
                                    showUpdateButton={false}
                                    targetDealerId={dealer.id}
                                    defaultValueDisabled
                                />
                            </Panel>
                        </CollapsibleWrapper>
                    </Form>
                )}
            </Formik>
        </GenericStateOnPageContextProvider>
    );
};

export default DealerAssignmentConfiguratorForm;
